/**
 * Comprehensive character mapping for transliteration to ASCII.
 * Covers common special characters from various languages that don't normalize well.
 * Characters are mapped before NFD normalization to handle special cases.
 *
 * Language Support:
 * - English: ✅ Full support (already ASCII)
 * - Portuguese: ✅ Full support (Latin-1 characters: á, ã, ç, é, ê, í, ó, ô, õ, ú)
 * - Spanish: ✅ Full support (Latin-1 characters: á, é, í, ñ, ó, ú)
 * - German (Deutsch): ✅ Full support (ß → ss, ä → ae, ö → oe, ü → ue)
 * - Russian: ✅ Full support (Cyrillic alphabet mapped to ASCII)
 * - Japanese: ⚠️ Limited support (Hiragana/Katakana/Kanji removed by fallback)
 * - Chinese: ⚠️ Limited support (Han characters removed by fallback)
 *
 * Note: Japanese and Chinese characters are removed by the fallback regex as they require
 * complex transliteration (Romaji for Japanese, Pinyin for Chinese) that would require
 * extensive character mappings or a specialized library.
 */
const CHAR_MAP = {
  // German Eszett (must be handled specially as it doesn't normalize)
  '\u00DF': 'ss', // ß
  '\u00C4': 'Ae', // Ä
  '\u00D6': 'Oe', // Ö
  '\u00DC': 'Ue', // Ü
  '\u00E4': 'ae', // ä
  '\u00F6': 'oe', // ö
  '\u00FC': 'ue', // ü

  // Latin-1 Supplement (ISO 8859-1) - lowercase
  '\u00E0': 'a', // à
  '\u00E1': 'a', // á
  '\u00E2': 'a', // â
  '\u00E3': 'a', // ã
  '\u00E5': 'a', // å
  '\u00E6': 'ae', // æ
  '\u00E7': 'c', // ç
  '\u00E8': 'e', // è
  '\u00E9': 'e', // é
  '\u00EA': 'e', // ê
  '\u00EB': 'e', // ë
  '\u00EC': 'i', // ì
  '\u00ED': 'i', // í
  '\u00EE': 'i', // î
  '\u00EF': 'i', // ï
  '\u00F0': 'd', // ð
  '\u00F1': 'n', // ñ
  '\u00F2': 'o', // ò
  '\u00F3': 'o', // ó
  '\u00F4': 'o', // ô
  '\u00F5': 'o', // õ
  '\u00F8': 'o', // ø
  '\u00F9': 'u', // ù
  '\u00FA': 'u', // ú
  '\u00FB': 'u', // û
  '\u00FD': 'y', // ý
  '\u00FE': 'th', // þ
  '\u00FF': 'y', // ÿ

  // Latin-1 Supplement (ISO 8859-1) - uppercase
  '\u00C0': 'A', // À
  '\u00C1': 'A', // Á
  '\u00C2': 'A', // Â
  '\u00C3': 'A', // Ã
  '\u00C5': 'A', // Å
  '\u00C6': 'Ae', // Æ
  '\u00C7': 'C', // Ç
  '\u00C8': 'E', // È
  '\u00C9': 'E', // É
  '\u00CA': 'E', // Ê
  '\u00CB': 'E', // Ë
  '\u00CC': 'I', // Ì
  '\u00CD': 'I', // Í
  '\u00CE': 'I', // Î
  '\u00CF': 'I', // Ï
  '\u00D0': 'D', // Ð
  '\u00D1': 'N', // Ñ
  '\u00D2': 'O', // Ò
  '\u00D3': 'O', // Ó
  '\u00D4': 'O', // Ô
  '\u00D5': 'O', // Õ
  '\u00D8': 'O', // Ø
  '\u00D9': 'U', // Ù
  '\u00DA': 'U', // Ú
  '\u00DB': 'U', // Û
  '\u00DD': 'Y', // Ý
  '\u00DE': 'Th', // Þ

  // Cyrillic (Russian) - uppercase
  '\u0410': 'A', // А
  '\u0411': 'B', // Б
  '\u0412': 'V', // В
  '\u0413': 'G', // Г
  '\u0414': 'D', // Д
  '\u0415': 'E', // Е
  '\u0416': 'Zh', // Ж
  '\u0417': 'Z', // З
  '\u0418': 'I', // И
  '\u0419': 'Y', // Й
  '\u041A': 'K', // К
  '\u041B': 'L', // Л
  '\u041C': 'M', // М
  '\u041D': 'N', // Н
  '\u041E': 'O', // О
  '\u041F': 'P', // П
  '\u0420': 'R', // Р
  '\u0421': 'S', // С
  '\u0422': 'T', // Т
  '\u0423': 'U', // У
  '\u0424': 'F', // Ф
  '\u0425': 'Kh', // Х
  '\u0426': 'Ts', // Ц
  '\u0427': 'Ch', // Ч
  '\u0428': 'Sh', // Ш
  '\u0429': 'Shch', // Щ
  '\u042A': '', // Ъ (hard sign, usually omitted)
  '\u042B': 'Y', // Ы
  '\u042C': '', // Ь (soft sign, usually omitted)
  '\u042D': 'E', // Э
  '\u042E': 'Yu', // Ю
  '\u042F': 'Ya', // Я

  // Cyrillic (Russian) - lowercase
  '\u0430': 'a', // а
  '\u0431': 'b', // б
  '\u0432': 'v', // в
  '\u0433': 'g', // г
  '\u0434': 'd', // д
  '\u0435': 'e', // е
  '\u0436': 'zh', // ж
  '\u0437': 'z', // з
  '\u0438': 'i', // и
  '\u0439': 'y', // й
  '\u043A': 'k', // к
  '\u043B': 'l', // л
  '\u043C': 'm', // м
  '\u043D': 'n', // н
  '\u043E': 'o', // о
  '\u043F': 'p', // п
  '\u0440': 'r', // р
  '\u0441': 's', // с
  '\u0442': 't', // т
  '\u0443': 'u', // у
  '\u0444': 'f', // ф
  '\u0445': 'kh', // х
  '\u0446': 'ts', // ц
  '\u0447': 'ch', // ч
  '\u0448': 'sh', // ш
  '\u0449': 'shch', // щ
  '\u044A': '', // ъ (hard sign, usually omitted)
  '\u044B': 'y', // ы
  '\u044C': '', // ь (soft sign, usually omitted)
  '\u044D': 'e', // э
  '\u044E': 'yu', // ю
  '\u044F': 'ya', // я
}

/**
 * Sanitizes a URL path by decoding URL-encoded characters and converting
 * Unicode characters to their ASCII equivalents.
 *
 * Example: /es/3d-modelos/arquitect%C3%B3nico -> /es/3d-modelos/arquitectonico
 * Example: au%C3%9Fen -> aussen
 *
 * @param {string} path - The path to sanitize
 * @returns {string} - The sanitized path
 */
export function sanitizePath(path) {
  if (!path || typeof path !== 'string') {
    return path
  }

  try {
    // Decode URL-encoded characters (e.g., %C3%B3 -> ó)
    const decoded = decodeURIComponent(path)

    // First, apply character mapping for special characters that don't normalize well
    let normalized = decoded
    for (const [char, replacement] of Object.entries(CHAR_MAP)) {
      normalized = normalized.replace(new RegExp(char, 'g'), replacement)
    }

    // Then use NFD normalization to handle remaining diacritics
    // This separates base characters from combining diacritical marks
    normalized = normalized
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove combining diacritical marks

    // Remove any remaining non-ASCII characters (fallback for characters not in our map)
    normalized = normalized.replace(/[^\x00-\x7F]/g, '')

    // Convert to lowercase for URL paths (URLs are typically case-insensitive but lowercase is standard)
    return normalized.toLowerCase()
  } catch (error) {
    // If decodeURIComponent fails (e.g., malformed URL), return original path
    return path
  }
}
